<template>
  <!-- The analysis chart under the replay board (SPEC §9): black's win rate or lead per position. -->
  <div class="winrate-chart">
    <canvas ref="canvas"
      class="winrate-canvas"
      :style="{ height: height + 'px' }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      />
  </div>
</template>

<script>
import Game from '@/game/Game';
import { t } from '@/texts';
import { Stone } from '@/game/Board';
import { mistakes, lastPosition } from '@/game/analysis';

const PADDING = 5;
const COLORS = {
  background: '#e0fae8',
  black: '#2b374b',
  white: '#e8a406',
  midline: '#9aa8a0',
  marker: '#f00',
  whiteText: '#b07c00', // the white line's colour, darker, for text on the background
};

export default {
  name: 'WinrateChart',

  props: {
    game: { type: Game, required: true },
    // The replay's cursor: the number of records shown, so the shown position is cursor − 1.
    cursor: { type: Number, required: true },
  },

  data() {
    return {
      width: 0,
      pressing: false,
    };
  },

  computed: {
    height() {
      return this.$store.state.isMobile ? 80 : 100;
    },

    // 'winrate' or 'lead', remembered on this device with the other settings.
    line() {
      return (this.$store.state.ui && this.$store.state.ui.chartLine) === 'lead' ? 'lead' : 'winrate';
    },

    // The last position: the one after the last stone (a resignation adds no position).
    last() {
      return lastPosition(this.game);
    },

    // KataGo's analysis of each position 0..last (null where there is none).
    values() {
      const v = new Array(this.last + 1).fill(null);
      for(const m of this.game.moves) {
        const a = m.analysis;
        if(a && a.engine && m.index <= this.last)
          v[m.index] = { winrate: a.winrate || 0, lead: a.scoreLead || 0 };
      }
      return v;
    },

    // The lead scale: the game's largest lead, rounded up to 10 points.
    leadScale() {
      let max = 0;
      for(const v of this.values) if(v) max = Math.max(max, Math.abs(v.lead));
      return Math.max(10, Math.ceil(max / 10) * 10);
    },

    // The moves that lost at least 5 points (SPEC §9), marked with a dot.
    mistakes() {
      return mistakes(this.game);
    },

    shown() {
      return Math.max(0, Math.min(this.cursor - 1, this.last));
    },
  },

  watch: {
    values() { this.draw(); },
    mistakes() { this.draw(); },
    shown() { this.draw(); },
    line() { this.draw(); },
    height() { this.$nextTick(this.resize); },
  },

  mounted() {
    this.resize();
    if(window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.resize());
      this.observer.observe(this.$el);
    } else {
      window.addEventListener('resize', this.resize);
    }
  },

  beforeDestroy() {
    if(this.observer) this.observer.disconnect();
    else window.removeEventListener('resize', this.resize);
  },

  methods: {
    resize() {
      const canvas = this.$refs.canvas;
      if(!canvas) return;
      const width = canvas.clientWidth;
      const ratio = window.devicePixelRatio || 1;
      this.width = width;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(this.height * ratio);
      this.draw();
    },

    x(p) {
      const w = this.width - 2 * PADDING;
      return PADDING + (this.last > 0 ? p * w / this.last : 0);
    },

    // y of a value: 0..1 for the win rate (0.5 in the middle), points for the lead (0 in the middle).
    y(v) {
      const h = this.height - 2 * PADDING;
      const f = this.line === 'lead' ? 0.5 + v.lead / this.leadScale / 2 : v.winrate;
      return PADDING + (1 - Math.max(0, Math.min(1, f))) * h;
    },

    draw() {
      const canvas = this.$refs.canvas;
      if(!canvas || !this.width) return;
      const ctx = canvas.getContext('2d');
      const ratio = canvas.width / this.width;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      const w = this.width;
      const h = this.height;
      const mid = PADDING + (h - 2 * PADDING) / 2;

      ctx.fillStyle = COLORS.background;
      ctx.fillRect(0, 0, w, h);

      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = COLORS.midline;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PADDING, mid);
      ctx.lineTo(w - PADDING, mid);
      ctx.stroke();
      ctx.restore();

      this.drawArea(ctx, mid, 0, mid, COLORS.black, 0.18);
      this.drawArea(ctx, mid, mid, h, COLORS.white, 0.3);
      this.drawLine(ctx);
      this.drawMistakes(ctx);
      this.drawMarker(ctx);
    },

    // Runs of positions with analysis, as lists of positions.
    runs() {
      const runs = [];
      let run = null;
      this.values.forEach((v, p) => {
        if(v) {
          if(!run) runs.push(run = []);
          run.push(p);
        } else {
          run = null;
        }
      });
      return runs;
    },

    // The area between the line and the middle, tinted in the leading colour (only the band
    // top..bottom is painted).
    drawArea(ctx, mid, top, bottom, color, alpha) {
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, top, this.width, bottom - top);
      ctx.clip();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      for(const run of this.runs()) {
        ctx.beginPath();
        ctx.moveTo(this.x(run[0]), mid);
        for(const p of run) ctx.lineTo(this.x(p), this.y(this.values[p]));
        ctx.lineTo(this.x(run[run.length - 1]), mid);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    },

    drawLine(ctx) {
      ctx.strokeStyle = COLORS.black;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      for(const run of this.runs()) {
        ctx.beginPath();
        run.forEach((p, i) => {
          const x = this.x(p), y = this.y(this.values[p]);
          if(i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        });
        if(run.length === 1) ctx.lineTo(this.x(run[0]) + 1, this.y(this.values[run[0]]));
        ctx.stroke();
      }
    },

    // A dot on the line at each mistake: black for black's, white with a dark border for white's.
    drawMistakes(ctx) {
      for(const k of this.mistakes) {
        const v = this.values[k.index];
        if(!v) continue;
        ctx.beginPath();
        ctx.arc(this.x(k.index), this.y(v), 3, 0, 2 * Math.PI);
        ctx.fillStyle = k.stone === Stone.WHITE ? '#fff' : '#000';
        ctx.fill();
        ctx.strokeStyle = COLORS.black;
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    },

    drawMarker(ctx) {
      const p = this.shown;
      const x = Math.round(this.x(p)) + 0.5;
      ctx.strokeStyle = COLORS.marker;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.height);
      ctx.stroke();

      const v = this.values[p];
      if(!v) return;

      ctx.beginPath();
      ctx.fillStyle = COLORS.black;
      ctx.arc(x, this.y(v), 3, 0, 2 * Math.PI);
      ctx.fill();

      const rate = t('chart-winrate', { rate: (v.winrate * 100).toFixed(1) });
      const leader = v.lead >= 0 ? 'color-black' : 'color-white';
      const lead = Math.abs(v.lead) < 0.5 ? t('chart-even')
        : t('chart-lead', { color: t(leader), points: Math.abs(v.lead).toFixed(1) });

      ctx.font = '13px Arial';
      const textWidth = Math.max(ctx.measureText(rate).width, ctx.measureText(lead).width);
      const right = x + 4 + textWidth <= this.width - PADDING;
      ctx.textAlign = right ? 'left' : 'right';
      const tx = right ? x + 4 : x - 4;

      // A light backing keeps the labels readable over the line.
      const left = right ? tx - 2 : tx - textWidth - 2;
      ctx.fillStyle = 'rgba(224, 250, 232, 0.8)';
      ctx.fillRect(left, 2, textWidth + 4, 17);
      ctx.fillRect(left, this.height - 19, textWidth + 4, 17);

      ctx.textBaseline = 'top';
      ctx.fillStyle = COLORS.black;
      ctx.fillText(rate, tx, 4);
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = Math.abs(v.lead) < 0.5 || leader === 'color-black' ? COLORS.black : COLORS.whiteText;
      ctx.fillText(lead, tx, this.height - 3);
    },

    // The position under x, clamped to 0..last.
    positionAt(x) {
      const w = this.width - 2 * PADDING;
      if(this.last === 0 || w <= 0) return 0;
      const p = Math.round((x - PADDING) * this.last / w);
      return Math.max(0, Math.min(this.last, p));
    },

    select(e) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      this.$emit('update:cursor', this.positionAt(e.clientX - rect.left) + 1);
    },

    onPointerDown(e) {
      this.pressing = true;
      if(e.pointerId !== undefined && this.$refs.canvas.setPointerCapture)
        this.$refs.canvas.setPointerCapture(e.pointerId);
      this.select(e);
    },

    onPointerMove(e) {
      if(this.pressing) this.select(e);
    },

    onPointerUp() {
      this.pressing = false;
    },

  }
};
</script>

<style scoped>
.winrate-chart {
  width: 100%;
}

.winrate-canvas {
  display: block;
  width: 100%;
  box-sizing: border-box;
  border: solid 1px #aaa;
  touch-action: none;
}
</style>
